import { useCallback, useEffect, useState } from "react";

import SparePartService from "../api/sparePart.service";

import {
  Pagination,
  SparePart,
  SparePartFilters,
  SparePartPayload,
  RecordStatus,
} from "../types";

const DEFAULT_FILTERS: SparePartFilters = {
  search: "",
  page: 1,
  limit: 10,
};

export const useSpareParts = () => {
  const [spareParts, setSpareParts] = useState<SparePart[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  const [filters, setFilters] =
    useState<SparePartFilters>(DEFAULT_FILTERS);

  const fetchSpareParts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response =
        await SparePartService.getSpareParts(filters);

      setSpareParts(response.items);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Failed to load spare parts."
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchSpareParts();
  }, [fetchSpareParts]);

  const createSparePart = async (
    payload: SparePartPayload
  ) => {
    await SparePartService.createSparePart(payload);
    await fetchSpareParts();
  };

  const updateSparePart = async (
    id: string,
    payload: SparePartPayload
  ) => {
    await SparePartService.updateSparePart(id, payload);
    await fetchSpareParts();
  };

  const deleteSparePart = async (id: string) => {
    await SparePartService.deleteSparePart(id);
    await fetchSpareParts();
  };

  const updateStatus = async (
    id: string,
    status: RecordStatus
  ) => {
    await SparePartService.updateStatus(id, status);
    await fetchSpareParts();
  };

  const updateFilters = (
    values: Partial<SparePartFilters>
  ) => {
    setFilters((prev) => ({
      ...prev,
      ...values,
    }));
  };

  return {
    spareParts,

    loading,

    error,

    pagination,

    filters,

    fetchSpareParts,

    createSparePart,

    updateSparePart,

    deleteSparePart,

    updateStatus,

    updateFilters,
  };
};